import { DateTime } from "luxon";
import { getOrParseDateTime } from "@cpt/shared";
import { formatDateOnly } from "utils/formatters/DateFormatters";
import type { OrderDto } from "client/api-v2";

export const termLengthMonths = 12;
const latestTermStartMonths = 12;
const latestQuoteExpiryMonths = 12;

export const termStartEndedMessage = "The term start date puts the whole 12-month term in the past.";
export const quoteExpiryPassedMessage = "The quote expiry date has already passed.";
export const quoteExpiryRequiredMessage = "Enter a quote expiry date, or choose 30 days from publish.";

export function termStartTooLateMessage(latest: string): string {
  return `The term start date can't be later than ${formatIsoDate(latest)}, 12 months from today.`;
}

export function termStartPastSavedStartMessage(savedStart: string): string {
  return `The term start date can't move past ${formatIsoDate(
    savedStart
  )}, because it's already more than 12 months away.`;
}

export function quoteExpiryTooLateMessage(latest: string): string {
  return `The quote expiry date can't be later than ${formatIsoDate(latest)}, 12 months from today.`;
}

type TermStartFields = Pick<OrderDto, "termStartDate">;
type QuoteExpiryFields = Pick<OrderDto, "validUntilOverride">;

export function todayUtc(): DateTime {
  return DateTime.utc().startOf("day");
}

// Why: the response interceptor makes dates Luxon DateTimes despite the `string` type.
export function toIsoDate(value: string | null): string | null {
  return value === null ? null : getOrParseDateTime(value).toISODate();
}

function formatIsoDate(isoDate: string): string {
  return formatDateOnly(getOrParseDateTime(isoDate));
}

function isoDateOf(date: DateTime): string {
  return date.toISODate() ?? "";
}

function later(first: string, second: string): string {
  return first > second ? first : second;
}

export function derivedTermEnd(termStartDate: string): string {
  const start = getOrParseDateTime(termStartDate);
  // Why: must match `Order.TermEndDate`, which is the term's last day.
  return start.isValid ? isoDateOf(start.plus({ months: termLengthMonths }).minus({ days: 1 })) : "";
}

export function earliestTermStart(today: DateTime = todayUtc()): string {
  return isoDateOf(today.minus({ months: termLengthMonths }).plus({ days: 1 }));
}

export function latestTermStart(saved: TermStartFields, today: DateTime = todayUtc()): string {
  return later(isoDateOf(today.plus({ months: latestTermStartMonths })), toIsoDate(saved.termStartDate) ?? "");
}

export function latestQuoteExpiry(today: DateTime = todayUtc()): string {
  return isoDateOf(today.plus({ months: latestQuoteExpiryMonths }));
}

export function termStartProblem(
  draft: TermStartFields,
  saved: TermStartFields,
  today: DateTime = todayUtc()
): string | undefined {
  const termStartDate = toIsoDate(draft.termStartDate);
  if (!termStartDate) {
    return undefined;
  }
  if (derivedTermEnd(termStartDate) < isoDateOf(today)) {
    return termStartEndedMessage;
  }
  const cap = isoDateOf(today.plus({ months: latestTermStartMonths }));
  const savedStart = toIsoDate(saved.termStartDate) ?? "";
  if (termStartDate <= later(cap, savedStart)) {
    return undefined;
  }
  return savedStart > cap ? termStartPastSavedStartMessage(savedStart) : termStartTooLateMessage(cap);
}

export function quoteExpiryProblem(draft: QuoteExpiryFields, today: DateTime = todayUtc()): string | undefined {
  if (draft.validUntilOverride === "") {
    return quoteExpiryRequiredMessage;
  }
  const validUntil = toIsoDate(draft.validUntilOverride);
  if (!validUntil) {
    return undefined;
  }
  if (validUntil < isoDateOf(today)) {
    return quoteExpiryPassedMessage;
  }
  const latest = latestQuoteExpiry(today);
  return validUntil > latest ? quoteExpiryTooLateMessage(latest) : undefined;
}
